/**
 * UC3 launch updates (client, 8 Oct 2026, item 1): "selecting a country or a watershed
 * fits the map to it and hides every other unit". The two pure questions behind that, kept
 * out of the components so they can be tested without a Leaflet map:
 *
 * * which unit features stay on the map under an isolation (`inIsolate`): a watershed
 *   isolation keeps that watershed alone, a country isolation keeps the units of that
 *   country (its own outline, and the watersheds a productivity map paints inside it);
 * * which feature *is* the isolated unit (`isolateFeature`), for its outline, its label and
 *   the raster clip.
 */
import type { UnitProperties, UnitsFC } from '../../lib/api';
import type { IntelIsolate } from '../../lib/state';

/** Does a unit with these properties stay on the map under `isolate`? */
export function inIsolate(props: Pick<UnitProperties, 'id' | 'country'>, isolate: IntelIsolate | null): boolean {
  if (!isolate) return true;
  if (isolate.level === 'watershed') return props.id === isolate.unitId;
  return props.id === isolate.unitId || props.country === isolate.country;
}

/** The isolated unit's own feature: a watershed from `watersheds`, a country from `admin0`. */
export function isolateFeature(
  isolate: IntelIsolate | null,
  admin0: UnitsFC | undefined,
  watersheds: UnitsFC | undefined,
): UnitsFC['features'][number] | null {
  if (!isolate) return null;
  if (isolate.level === 'watershed') {
    return watersheds?.features.find((f) => f.properties.id === isolate.unitId) ?? null;
  }
  return (
    admin0?.features.find((f) => f.properties.id === isolate.unitId) ??
    admin0?.features.find((f) => f.properties.country === isolate.country) ??
    null
  );
}
